import { useState, useRef, useEffect } from 'react';

interface Message {
  id: number;
  type: 'user' | 'bot';
  text: string;
}

const initialMessages: Message[] = [
  {
    id: 1,
    type: 'bot',
    text: '您好！我是 Firmai 智能助手，很高兴为您服务。请问有什么可以帮助您的？',
  },
];

const quickReplies = [
  '平台功能介绍',
  '预约演示',
  '价格咨询',
  '技术支持',
];

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = (text: string) => {
    if (!text.trim()) return;

    const userMsg: Message = {
      id: Date.now(),
      type: 'user',
      text: text.trim(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');

    // Simulate bot response
    setTimeout(() => {
      const botResponses: Record<string, string> = {
        '平台功能介绍': 'Firmai 平台提供六大核心功能：实时监测仪表盘、AI智能分析、预测性维护、碳排放追踪、企业工具集成和智能报告生成。您可以在首页查看详细介绍，或预约演示深入了解。',
        '预约演示': '您可以通过页面底部的预约表单提交演示申请，我们的专业顾问将在24小时内与您联系。也可以直接拨打 18916035855 咨询。',
        '价格咨询': 'Firmai 提供灵活的定价方案，根据企业规模和功能需求定制。建议您预约演示，我们的顾问会为您提供详细的报价方案。',
        '技术支持': '我们的技术支持团队7x24小时在线。您可以通过邮件 support@firmai.com 或拨打客服热线获取帮助。',
      };

      const botText = botResponses[text.trim()] ||
        '感谢您的咨询！我们的专业团队会尽快为您解答。您也可以拨打 18916035855 或填写预约表单获取更详细的信息。';

      const botMsg: Message = {
        id: Date.now() + 1,
        type: 'bot',
        text: botText,
      };

      setMessages((prev) => [...prev, botMsg]);
    }, 800);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
          isOpen
            ? 'bg-firmai-card border border-firmai-border text-white rotate-0'
            : 'bg-firmai-green text-firmai-dark hover:scale-110'
        }`}
      >
        <span className="w-6 h-6 flex items-center justify-center">
          <i className={`${isOpen ? 'ri-close-line' : 'ri-customer-service-2-line'} text-xl`} />
        </span>
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-48px)] bg-firmai-card border border-firmai-border rounded-xl shadow-2xl overflow-hidden flex flex-col" style={{ height: '480px' }}>
          {/* Header */}
          <div className="px-5 py-4 border-b border-firmai-border bg-firmai-dark">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-firmai-green/20 rounded-full">
                <span className="w-5 h-5 flex items-center justify-center text-firmai-green">
                  <i className="ri-robot-2-line" />
                </span>
              </div>
              <div>
                <h4 className="text-white font-medium text-sm">Firmai 智能助手</h4>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-firmai-green rounded-full animate-pulse" />
                  <span className="text-firmai-muted text-xs">在线</span>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-xl text-sm leading-relaxed ${
                    msg.type === 'user'
                      ? 'bg-firmai-green text-firmai-dark'
                      : 'bg-firmai-dark border border-firmai-border text-firmai-text'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Quick Replies */}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {quickReplies.map((reply) => (
                  <button
                    key={reply}
                    onClick={() => handleSend(reply)}
                    className="px-3 py-1.5 bg-firmai-dark border border-firmai-border rounded-full text-firmai-text text-xs hover:border-firmai-green hover:text-firmai-green transition-all duration-200"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-firmai-border bg-firmai-dark">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSend(inputValue);
                }}
                placeholder="输入您的问题..."
                className="flex-1 px-4 py-2.5 bg-firmai-card border border-firmai-border rounded-lg text-white text-sm placeholder-firmai-muted focus:border-firmai-green focus:outline-none transition-colors"
              />
              <button
                onClick={() => handleSend(inputValue)}
                className="w-10 h-10 flex items-center justify-center bg-firmai-green text-firmai-dark rounded-lg hover:bg-firmai-green/90 transition-colors shrink-0"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-send-plane-fill" />
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}